/**
 * TeamListItem - Single team row in the left panel
 * 
 * Shows: status dot, name, quality badge (Qxx), x/y filled, start date
 * Draggable between projects. "..." context menu.
 */

import React, { useState, useRef } from 'react';
import { ContextMenu, type MenuAction } from './ContextMenu';

interface TeamData {
  id: number;
  name: string;
  status: string;
  start_date?: string;
  member_count_desired?: number;
  // Computed fields
  filled_count?: number;
  position_count?: number;
  quality_score?: number;
  team_strength?: number;
}

interface TeamListItemProps {
  team: TeamData;
  isSelected?: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onShare: () => void;
  onArchive: () => void;
  onMoveToProject: () => void;
  projectId?: number;
  /** Optional subtitle (e.g. project name for archived teams in the Archived section) */
  subtitle?: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-500',
  planning: 'bg-blue-500',
  paused: 'bg-amber-500',
  complete: 'bg-gray-400',
  archived: 'bg-gray-600',
};

function getQualityColor(score: number): string {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-blue-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-red-400';
}

function formatStartDate(date?: string): string {
  if (!date) return '';
  // Handle both full ISO and YYYY-MM formats
  return date.substring(0, 7); // YYYY-MM
}

export const TeamListItem: React.FC<TeamListItemProps> = ({
  team,
  isSelected = false,
  onSelect,
  onEdit,
  onShare,
  onArchive,
  onMoveToProject,
  projectId,
  subtitle,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const filledCount = team.filled_count || 0;
  const positionCount = team.position_count || 0;
  const qualityScore = team.quality_score;
  const teamStrength = team.team_strength;
  const startDate = formatStartDate(team.start_date);

  const menuActions: MenuAction[] = [
    { id: 'edit', label: 'Edit Team', icon: '/', onClick: () => { setShowMenu(false); onEdit(); } },
    { id: 'share', label: 'Share', icon: '+', onClick: () => { setShowMenu(false); onShare(); } },
    { id: 'move', label: 'Move to Project', icon: '>', onClick: () => { setShowMenu(false); onMoveToProject(); } },
    { id: 'divider', label: '', onClick: () => {} },
    { id: 'archive', label: 'Archive', onClick: () => { setShowMenu(false); onArchive(); }, warning: true },
    { id: 'delete', label: 'Delete', onClick: () => {
      setShowMenu(false);
      if (window.confirm(`Delete team "${team.name}"? This cannot be undone.`)) {
        onArchive(); // Delete currently archives the team (soft delete)
      }
    }, danger: true },
  ];

  // Drag handlers
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/team-id', String(team.id));
    e.dataTransfer.setData('text/source-project-id', String(projectId ?? 0));
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div>
      <div
        data-testid={`team-list-item-${team.id}`}
        className={`flex flex-col px-2.5 py-1.5 mx-0.5 rounded cursor-pointer group transition-all ${
          isSelected
            ? 'bg-slate-800 border-l-2 border-indigo-500'
            : 'hover:bg-gray-800/40 border-l-2 border-transparent'
        }`}
        onClick={onSelect}
        draggable
        onDragStart={handleDragStart}
      >
        {/* Line 1: status dot, name, quality */}
        <div className="flex items-center gap-1.5">
          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_COLORS[team.status] || 'bg-gray-500'}`} />
          <span className={`text-[11px] flex-1 truncate ${isSelected ? 'text-white font-semibold' : 'text-gray-300'}`}>
            {team.name}
          </span>
          {qualityScore !== undefined && (
            <span className={`text-[9px] font-medium flex-shrink-0 ${getQualityColor(qualityScore)}`}>
              Q{qualityScore}
            </span>
          )}
          {/* Context menu button - always visible */}
          <button
            ref={menuBtnRef}
            data-testid={`team-menu-${team.id}`}
            aria-label="Team menu"
            className="text-gray-500 hover:text-gray-300 text-[10px] px-0.5 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              const rect = menuBtnRef.current?.getBoundingClientRect() || null;
              setMenuAnchor(rect);
              setShowMenu(!showMenu);
            }}
          >
            ...
          </button>
        </div>

        {/* Line 2: subtitle (e.g. project name) and/or filled count, strength, start date */}
        <div className="flex items-center gap-1 ml-3 flex-wrap">
          {subtitle && (
            <span className="text-gray-600 text-[9px] italic truncate max-w-full">{subtitle}</span>
          )}
          <span className="text-gray-500 text-[9px]">
            {filledCount}/{positionCount} filled
          </span>
          {teamStrength !== undefined && teamStrength > 0 && (
            <span className={`text-[9px] font-medium ${
              teamStrength >= 80 ? 'text-green-400' :
              teamStrength >= 60 ? 'text-yellow-400' :
              'text-red-400'
            }`}>
              {teamStrength}%
            </span>
          )}
          {startDate && (
            <>
              <span className="text-gray-600 text-[9px]">-</span>
              <span className="text-gray-500 text-[9px]">{startDate}</span>
            </>
          )}
        </div>
      </div>

      {/* Context Menu - fixed position, rendered outside scroll container */}
      {showMenu && (
        <ContextMenu
          actions={menuActions}
          onClose={() => setShowMenu(false)}
          anchorRect={menuAnchor}
        />
      )}
    </div>
  );
};
